import React, { useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  Paper,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { saveAs } from 'file-saver';

import { LoaderBox, SkeletonData } from '../../../components/Loader';
import AdminLayout from '../../../layouts/AdminLayout';
import Report, {grade} from '../../../models/Report';


const useStyles = makeStyles((theme) => ({
  nameColumn: {
    minWidth: '200px',
    left: 0,
    zIndex: '99 !important' as any,
  },
  headers: {
    fontWeight: 'bold',
  },
  container: {
    marginTop: 20,
    height: '78vh',
    width: 'auto',
    overflow: 'hidden !important' as any,
    position: 'relative',
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stickyColumn: {
    position: 'sticky',
    background: '#fafafa',
    left: 0,
    zIndex: 1,
  },
  formControl: {
    margin: theme.spacing(1),
    marginTop: 10,
    minWidth: 120,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const AdminReports = (props: RouteComponentProps<{ course_id: string, course_name: string }>) => {
  const classes = useStyles();
  const { course_id: courseID, course_name } = props.match.params;
  const [columns, setColumns] = useState(SkeletonData.Reports.columns as string[]);
  const [rows, setRows] = useState(SkeletonData.Reports.rows as any);
  const [sort, setSort] = useState('' as string);
  const [loading, setLoading] = useState(true as boolean);
  const [response, setResponse] = useState('' as string);


  const getReport = async () => {
    let data = {} as any;
    const c = [] as string[];
    const r = {} as any;

    try {
      data = await Report.getAll({courseID});
    } catch(e) {
      setResponse('error');
      return;
    }

    for(let i = 0; i < Object.keys(data).length; i++) {
      let keys = Object.keys(data);
      let key = keys[i];
      let grades = data[key];
      c.push(key);

      grades.forEach((grade: grade) => {
        let g = r[`${grade.name}-${grade.userId}`] as any[] || [] as any[];
        const today = new Date();
        const updatedTime = new Date(grade.updateTime);
        const startWeekDate = new Date();
        const endWeekDate = new Date();
        startWeekDate.setDate(today.getDate() - today.getDay());
        endWeekDate.setDate(startWeekDate.getDate() + 7);

        if(updatedTime >= startWeekDate && updatedTime <= endWeekDate && (grade.state === 'TURNED_IN' || grade.state === 'RETURNED')) {
          grade.grade = grade.grade + ' ✅';
        }

        g.push(`${key.split('-')[0]}-${grade.grade}`);

        if(g.length === keys.length) {
          g.sort((a, b) => Number(a.split('-')[0]) - Number(b.split('-')[0]));
        }
        r[`${grade.name}-${grade.userId}`] = g;
      });
    }

    c.sort((a: string, b: string) =>  Number(a.split('-')[0]) - Number(b.split('-')[0]));

    setColumns(c);
    setRows(r);
    
    setLoading(false);

    if(c.length === 0 || Object.keys(r).length === 0) {
      setResponse('empty');
    }
  };

  const handleSortChange = (event: any) => {
    const sort = event.target.value;
    const r = Object.keys(rows);
    r.sort((_a: string, _b: string) => {
      let a = _a.split(' ');
      let b = _b.split(' ');
      if(sort === 'lastname') {
        return a[a.length - 1].localeCompare(b[b.length - 1]);
      }
      return a[0].localeCompare(b[0]);
    });

    var objSorted = {} as any;
    r.forEach((key: string) => {
        objSorted[key]=rows[key];
    });

    setRows(objSorted);
    setSort(event.target.value);
  };

  const exportCSV = () => {
    const keys = Object.keys(rows);
    const r  = keys.map(k => [k.split('-')[0], ...rows[k].map((r: string) => r.split('-')[1])].join(','));
    const data = [['Students', ...columns.map(c => c.substr(c.indexOf('-')+ 1, c.length).replaceAll(',', ''))].join(','), ...r].join('\n');
    var blob = new Blob([data], {type: 'text/plain;charset=utf-8'});
    saveAs(blob, `${new Date().toLocaleDateString().replaceAll('/', '-')}-${course_name.replace(/[^A-Za-z0-9]/g, '-').replaceAll('--', '-')}-Grade-Report.csv`);
  };

  useEffect(() => {
    getReport();
  }, []);

  return (
    <AdminLayout>
        <Paper className={classes.container}>
        {response === 'error'?
            <p>Something went wrong.</p>
        :response === 'empty'?
            <p>No data available.</p>
        :<LoaderBox show={loading}>
          <TableContainer style={{position: 'absolute', height: '78vh', top: 0, left: 0}}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.nameColumn} component="th" scope="row" >
                  <Button size="small" variant="contained" onClick={() => exportCSV()}>Export as CSV</Button>
                  <br/>
                  <FormControl className={classes.formControl}>
                  <InputLabel id="sort">Sort by</InputLabel>
                  <Select
                    labelId="sort"
                    id="sort"
                    value={sort}
                    onChange={handleSortChange}
                  >
                    <MenuItem value={'lastname'}>Lastname</MenuItem>
                    <MenuItem value={'firstname'}>Firstname</MenuItem>
                  </Select>
                </FormControl>
                </TableCell>
                {
                  columns.map((title: string, i) => {
                    title = title.substr(title.indexOf('-')+1, title.length);
                    return <TableCell key={title+i} className={classes.headers}><span>{title}</span></TableCell>;
                  })
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                Object.keys(rows).map((key: string, i) => {
                  return (
                    <TableRow key={key+i}>
                      <TableCell className={classes.stickyColumn} component="th" scope="row">
                          <span>{key.split('-')[0]}</span>
                      </TableCell>
                      {rows[key].map((grade: any, j: any)  => 
                        <TableCell key={grade+j} component="th" scope="row">
                          <span>{grade.split('-')[1]}</span>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
      </TableContainer>
      </LoaderBox>
      }
      </Paper>
    </AdminLayout>
  );
};

export default withRouter(AdminReports);
